/**
 * C14 — site-owners inventory. Pure, deterministic aggregation over per-site owner
 * scans, plus a client-side CSV export. Each site carries two owner lists: its
 * Microsoft 365 group `owners`, and its SharePoint `siteOwners` (owner-role
 * identities from the site's permissions).
 */
import type { SiteRef, SiteOwner } from '@/graph/modules/sites';

export interface SiteOwnersRecord {
  site: SiteRef;
  /** Microsoft 365 group owners (the people who own a group-connected site). */
  owners: SiteOwner[];
  /** SharePoint "Site Owners" — owner-role identities from the site's permissions. */
  siteOwners: SiteOwner[];
}

export interface SiteOwnersResult {
  sitesScanned: number;
  /** Sites where neither a group owner nor a SharePoint site owner could be read. */
  sitesWithNoOwner: number;
  /** Total group-owner rows across all sites. */
  totalOwnerAssignments: number;
  /** Distinct group owners across the whole selection (de-duplicated by identity). */
  distinctOwners: number;
  records: SiteOwnersRecord[];
}

function ownerKey(o: SiteOwner): string {
  return `${o.type}:${(o.id ?? o.displayName).toLowerCase()}`;
}

function ownerless(rec: SiteOwnersRecord): boolean {
  return rec.owners.length === 0 && rec.siteOwners.length === 0;
}

/** Aggregate per-site owner scans into headline counts + a stable, sorted list. */
export function aggregateSiteOwners(records: SiteOwnersRecord[]): SiteOwnersResult {
  const distinct = new Set<string>();
  let totalOwnerAssignments = 0;
  let sitesWithNoOwner = 0;

  for (const rec of records) {
    if (ownerless(rec)) sitesWithNoOwner += 1;
    totalOwnerAssignments += rec.owners.length;
    for (const o of rec.owners) distinct.add(ownerKey(o));
  }

  // Sites with no owner at all surface first (the actionable gap), then by name.
  const sorted = [...records].sort((a, b) => {
    const ao = ownerless(a), bo = ownerless(b);
    if (ao && !bo) return -1;
    if (bo && !ao) return 1;
    return a.site.displayName.localeCompare(b.site.displayName);
  });

  return {
    sitesScanned: records.length,
    sitesWithNoOwner,
    totalOwnerAssignments,
    distinctOwners: distinct.size,
    records: sorted,
  };
}

function esc(v: string): string {
  // RFC-4180: quote when the value contains a comma, quote, or newline.
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** "Jane Doe <jane@contoso.com>; HR Owners" — a readable, joined owner list. */
export function formatOwnerList(owners: readonly SiteOwner[]): string {
  return owners.map((o) => (o.email ? `${o.displayName} <${o.email}>` : o.displayName)).join('; ');
}

/**
 * One row per site, with Owners (Microsoft 365 group) and Site Owners (SharePoint)
 * as separate columns so the two are easy to compare in a spreadsheet.
 */
export function ownersToCsv(records: readonly SiteOwnersRecord[]): string {
  const header = ['Site', 'Web URL', 'Owner (primary)', 'Site Owners (SharePoint)'];
  const rows: string[] = [header.join(',')];
  for (const { site, owners, siteOwners } of records) {
    rows.push([site.displayName, site.webUrl, formatOwnerList(owners), formatOwnerList(siteOwners)].map(esc).join(','));
  }
  return rows.join('\r\n');
}
